import { ChevronDown } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";

interface AccordionContextValue {
  value: string[];
  onValueChange: (value: string[]) => void;
}

const AccordionContext = React.createContext<AccordionContextValue | null>(
  null,
);

const useAccordion = () => {
  const context = React.useContext(AccordionContext);
  if (!context) {
    throw new Error("useAccordion must be used within Accordion");
  }
  return context;
};

interface AccordionProps {
  value: string[];
  onValueChange: (value: string[]) => void;
  children: React.ReactNode;
  className?: string;
}

export const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  ({ value, onValueChange, children, className }, ref) => {
    return (
      <AccordionContext.Provider value={{ value, onValueChange }}>
        <div ref={ref} className={cn("space-y-2", className)}>
          {children}
        </div>
      </AccordionContext.Provider>
    );
  },
);
Accordion.displayName = "Accordion";

interface AccordionItemProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

interface AccordionItemContextValue {
  value: string;
}

const AccordionItemContext =
  React.createContext<AccordionItemContextValue | null>(null);

interface AccordionTriggerProps
  extends Omit<React.ComponentPropsWithoutRef<"button">, "type" | "onClick"> {
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  onClick?: React.ComponentPropsWithoutRef<"button">["onClick"];
}

export const AccordionTrigger = React.forwardRef<
  HTMLButtonElement,
  AccordionTriggerProps
>(({ children, className, disabled, onClick, ...props }, ref) => {
  const { value: openValues, onValueChange } = useAccordion();
  const item = React.useContext(AccordionItemContext);

  if (!item) {
    throw new Error("AccordionTrigger must be used within AccordionItem");
  }

  const isOpen = openValues.includes(item.value);

  const handleClick: React.ComponentPropsWithoutRef<"button">["onClick"] = (
    e,
  ) => {
    if (disabled) return;

    onClick?.(e);
    if (e?.defaultPrevented) return;

    if (isOpen) {
      onValueChange(openValues.filter((v) => v !== item.value));
    } else {
      onValueChange([...openValues, item.value]);
    }
  };

  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      onClick={handleClick}
      {...props}
      className={cn(
        "flex w-full items-center justify-between p-4 text-left font-medium transition-all hover:bg-gray-50",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
    >
      {children}
      <ChevronDown
        className={cn(
          "h-4 w-4 shrink-0 text-gray-500 transition-transform duration-200",
          isOpen && "rotate-180",
        )}
      />
    </button>
  );
});
AccordionTrigger.displayName = "AccordionTrigger";

// AccordionItem with context support
const AccordionItemWithContext = React.forwardRef<
  HTMLDivElement,
  AccordionItemProps
>(({ value, children, className }, ref) => {
  return (
    <AccordionItemContext.Provider value={{ value }}>
      <div
        ref={ref}
        data-value={value}
        className={cn(
          "overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm",
          className,
        )}
      >
        {children}
      </div>
    </AccordionItemContext.Provider>
  );
});
AccordionItemWithContext.displayName = "AccordionItem";

// Export the context-aware version
export { AccordionItemWithContext as AccordionItem };

interface AccordionContentProps {
  children: React.ReactNode;
  className?: string;
}

export const AccordionContent = React.forwardRef<
  HTMLDivElement,
  AccordionContentProps
>(({ children, className }, ref) => {
  const { value: openValues } = useAccordion();
  const item = React.useContext(AccordionItemContext);

  if (!item) {
    throw new Error("AccordionContent must be used within AccordionItem");
  }

  const isOpen = openValues.includes(item.value);

  if (!isOpen) return null;

  return (
    <div
      ref={ref}
      className={cn(
        "overflow-hidden border-t border-gray-200 bg-white transition-all",
        className,
      )}
    >
      <div className="p-4">{children}</div>
    </div>
  );
});
AccordionContent.displayName = "AccordionContent";
